/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, May 2022
 * @format
 * @flow
 */

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import FireboardUtils from '../../../../../../util/fireboard-util';
import { Divider } from '../../../../../TableDelivery/components/Divider';
import CommonUtil from '../../../../common';
import CheckCard from '../CheckCard';
import classes from './ChecksWithGroup.module.css';

const SOURCETYPE = 'sourceType';
const ORDERTYPE = 'orderType';
const REVENUECENTER = 'revenueCenter';
const SENSORGROUP = 'sensorGroup';
const SOURCETYPE_ARRAY = ['Curbside', 'Online', 'Walk up', 'Call in', 'Kiosk', 'Scan to pay', 'Table', 'Tab'];

const ChecksWithGroup = props => {
  const {
    checks,
    selectedCheck,
    checkFilters,
    onSelectCheck,
    sensorsMap,
    managerMode,
    currency,
    fbVenueMode,
  } = props;

  const [groupChecks, setGroupChecks] = useState(new Map());
  const { data: storeInfo } = useSelector(state => state.storeInfo);

  useEffect(() => {
    filterChecks();
  }, [checks, checkFilters]);

  const filterChecks = () => {
    const { activeCheckFilter } = checkFilters;
    let checksMap = new Map();
    switch (activeCheckFilter) {
      case SOURCETYPE:
        checksMap = filterBasedOnODOrderType();
        break;
      case ORDERTYPE:
        checksMap = filterBasedOnPOSOrderType();
        break;
      case REVENUECENTER:
        checksMap = filterBasedOnRevenueCenter();
        break;
      case SENSORGROUP:
        checksMap = filterBasedOnSensorGroup();
        break;
      default: break;
    }
    setGroupChecks(checksMap);
  };

  const filterBasedOnODOrderType = () => {
    const { orderType, checkSourceTypes } = checkFilters;
    let checksMap = new Map();
    if (isAllOptionIncluded()) {
      if (checkSourceTypes && Array.isArray(checkSourceTypes) && checkSourceTypes.length > 0) {
        //remove 'All' label from list - if available
        const selectedTypes = checkSourceTypes.filter(type => type !== 'All');
        selectedTypes.forEach(srcTypeName => checksMap.set(srcTypeName, []));
      } else {
        SOURCETYPE_ARRAY.forEach(srcTypeName => checksMap.set(srcTypeName, []));
      }
    } else if (Array.isArray(orderType)) {
      const selectedTypeNames = orderType.map(op => op.value);
      selectedTypeNames.sort((a, b) => (
        SOURCETYPE_ARRAY.indexOf(a) - SOURCETYPE_ARRAY.indexOf(b)
      ));
      selectedTypeNames.forEach(eachType => checksMap.set(eachType, []));
    }

    checks.forEach(check => {
      const sourceTypeName = CommonUtil.getOrderTypeNameFromSourceType(check.sourceType, check.tab);
      if (sourceTypeName) {
        const existingChecks = checksMap.has(sourceTypeName) ? checksMap.get(sourceTypeName) : [];
        const newChecks = [...existingChecks, check];
        checksMap.set(sourceTypeName, newChecks);
      }
    });

    return checksMap;
  };

  const filterBasedOnPOSOrderType = () => {
    const { orderType, checkOrderTypes } = checkFilters;
    let checksMap = new Map();
    const { orderTypes } = storeInfo;
    let orderTypesLookup = {};
    orderTypes.forEach(eachType => orderTypesLookup[eachType.id] = eachType.name);
    const sortedOrderTypeNamesArr = getSortedRefsArray(orderTypes);
    if (isAllOptionIncluded()) {
      if (checkOrderTypes && Array.isArray(checkOrderTypes) && checkOrderTypes.length) {
        //remove 'All' label from list - if available
        const selectedTypes = checkOrderTypes.filter(type => type !== 'All');
        selectedTypes.sort((a, b) => (
          sortedOrderTypeNamesArr.indexOf(a) - sortedOrderTypeNamesArr.indexOf(b)
        ));
        selectedTypes.forEach(odrTypeName => checksMap.set(odrTypeName, []));
      } else {
        sortedOrderTypeNamesArr.forEach(odrTypeName => checksMap.set(odrTypeName, []));
      }
    } else {
      const selectedTypeNames = orderType.map(op => op.value);
      selectedTypeNames.sort((a, b) => (
        sortedOrderTypeNamesArr.indexOf(a) - sortedOrderTypeNamesArr.indexOf(b)
      ));
      selectedTypeNames.forEach(eachType => checksMap.set(eachType, []));
    }

    checks.forEach(check => {
      const orderTypeName = orderTypesLookup[check.orderTypeId];
      if (orderTypeName) {
        const existingChecks = checksMap.has(orderTypeName) ? checksMap.get(orderTypeName) : [];
        const newChecks = [...existingChecks, check];
        checksMap.set(orderTypeName, newChecks);
      }
    });

    return checksMap;
  };

  const filterBasedOnRevenueCenter = () => {
    const { orderType, checkRevenueTypes } = checkFilters;
    let checksMap = new Map();
    const { revenueCenters } = storeInfo;
    let revenueCentersLookup = {};
    revenueCenters.forEach(eachType => revenueCentersLookup[eachType.id] = eachType.name);
    const sortedRevCenterNamesArr = getSortedRefsArray(revenueCenters);
    if (isAllOptionIncluded()) {
      if (checkRevenueTypes && Array.isArray(checkRevenueTypes) && checkRevenueTypes.length) {
        //remove 'All' label from list - if available
        const selectedTypes = checkRevenueTypes.filter(type => type !== 'All');
        selectedTypes.sort((a, b) => (
          sortedRevCenterNamesArr.indexOf(a) - sortedRevCenterNamesArr.indexOf(b)
        ));
        selectedTypes.forEach(rCenterName => checksMap.set(rCenterName, []));
      } else {
        sortedRevCenterNamesArr.forEach(rCenterName => checksMap.set(rCenterName, []));
      }
    } else {
      const selectedTypeNames = orderType.map(op => op.value);
      selectedTypeNames.sort((a, b) => (
        sortedRevCenterNamesArr.indexOf(a) - sortedRevCenterNamesArr.indexOf(b)
      ));
      selectedTypeNames.forEach(eachType => checksMap.set(eachType, []));
    }

    checks.forEach(check => {
      const revenueCenterName = revenueCentersLookup[check.revenueCenterId];
      if (revenueCenterName) {
        const existingChecks = checksMap.has(revenueCenterName) ? checksMap.get(revenueCenterName) : [];
        const newChecks = [...existingChecks, check];
        checksMap.set(revenueCenterName, newChecks);
      }
    });

    return checksMap;
  };

  const filterBasedOnSensorGroup = () => {
    const { orderType, checkSensorGroupTypes } = checkFilters;
    let checksMap = new Map();
    const { sensorGroups } = storeInfo;
    let sensorGroupLookup = new Map();
    if (isAllOptionIncluded()) {
      if (checkSensorGroupTypes && Array.isArray(checkSensorGroupTypes) && checkSensorGroupTypes.length > 0) {
        //remove 'All' label from list - if available
        const selectedTypes = checkSensorGroupTypes.filter(type => type !== 'All');
        selectedTypes.sort(FireboardUtils.sortByValue);
        selectedTypes.forEach(sensorGrpName => {
          const hasSensorGroup = sensorGroups.find(sensorGrp => (sensorGrp.name).toLowerCase().trim() === sensorGrpName.toLowerCase().trim());
          if (hasSensorGroup && hasSensorGroup?.id) {
            sensorGroupLookup.set(sensorGrpName, hasSensorGroup.id);
            checksMap.set(sensorGrpName, []);
          }
        });
      } else {
        const sensorGroupList = [...sensorGroups];
        sensorGroupList.sort(FireboardUtils.sortByName);
        sensorGroupList.forEach(sensorGrp => {
          sensorGroupLookup.set(sensorGrp.name, sensorGrp.id);
          checksMap.set(sensorGrp.name, []);
        });
      }
    } else {
      const selectedTypes = orderType.map(op => op.value);
      selectedTypes.sort(FireboardUtils.sortByValue);
      selectedTypes.forEach(sensorGrpName => {
        const hasSensorGroup = sensorGroups.find(sensorGrp => (sensorGrp.name).toLowerCase().trim() === sensorGrpName.toLowerCase().trim());
        if (hasSensorGroup && hasSensorGroup?.id) {
          sensorGroupLookup.set(sensorGrpName, hasSensorGroup.id);
          checksMap.set(sensorGrpName, []);
        }
      });
    }

    Array.from(sensorGroupLookup.entries()).map(([key, value]) => {
      const checkArr = CommonUtil.getSensorGroupedChecks(checks, sensorsMap || new Map(), [value]);
      if (checkArr && Array.isArray(checkArr) && checkArr.length) {
        const existingChecks = checksMap.has(key) ? checksMap.get(key) : [];
        const newChecks = [...existingChecks, ...checkArr];
        checksMap.set(key, newChecks);
      }
    });

    return checksMap;
  };

  const isAllOptionIncluded = () => {
    const { orderType } = checkFilters;
    let isAllIncluded = false;
    if (orderType === null ||
      (Array.isArray(orderType) && orderType.length === 0) ||
      (Array.isArray(orderType) && orderType.length === 1 && orderType[0].value === 'All')
    ) {
      isAllIncluded = true;
    }
    return isAllIncluded;
  };

  const sortBySortOrderKey = (a, b) => {
    const sortOrder1 = a.sortOrder;
    const sortOrder2 = b.sortOrder;
    return sortOrder1 - sortOrder2;
  };

  const getSortedRefsArray = referenceArray => {
    const refsArray = referenceArray || [];
    refsArray.sort(sortBySortOrderKey);
    const refsArrayNames = refsArray.map(eachItem => eachItem.name);
    return refsArrayNames;
  };

  return (
    <>
      {Array.from(groupChecks.entries()).map(([key, checkArray]) =>
        <React.Fragment key={key}>
          <Divider title={key} />
          {checkArray?.length === 0 ? <NoSectionCheck /> : (
            <div className={classes.checkCardsWrapper}>
              {checkArray.map((check, index) =>
                <CheckCard
                  key={`check - ${check.id}`}
                  check={check}
                  onSelectCheck={onSelectCheck}
                  isSelected={check?.id === selectedCheck?.id}
                  rowIndex={index}
                  managerMode={managerMode}
                  currency={currency}
                  fbVenueMode={fbVenueMode}
                />
              )}
            </div>
          )}
        </React.Fragment>
      )}
    </>
  );
};

export default ChecksWithGroup;

ChecksWithGroup.propTypes = {
  checks: PropTypes.array,
  selectedCheck: PropTypes.object,
  checkFilters: PropTypes.object,
  onSelectCheck: PropTypes.func,
  sensorsMap: PropTypes.instanceOf(Map),
  managerMode: PropTypes.bool,
  currency: PropTypes.string,
  fbVenueMode: PropTypes.bool,
};

ChecksWithGroup.defaultProps = {
  checks: [],
  selectedCheck: {},
  checkFilters: {},
  onSelectCheck: () => { },
  sensorsMap: new Map(),
  managerMode: false,
  currency: '$',
  fbVenueMode: false,
};

const NoSectionCheck = () => (
  <div className={classes.noSectionCheck}>
    <span>No checks</span>
  </div>
);
